import React, { useState } from 'react';
import { Send, Phone, Mail, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(''); // 'idle', 'sending', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Replace 'YOUR_FORM_ID' with your actual Formspree form ID
  const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ID
    ? `https://formspree.io/f/${process.env.REACT_APP_FORMSPREE_ID}`
    : 'https://formspree.io/f/YOUR_FORM_ID';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      });

      if (response.ok) {
        setStatus('success');
        setForm({ name: '', email: '', message: '' });
        // Reset status after 5 seconds
        setTimeout(() => {
          setStatus('idle');
        }, 5000);
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="pt-24 pb-20 bg-slate-50 dark:bg-background min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="grid md:grid-cols-2 gap-12 md:gap-16"
        >

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 dark:text-text-primary mb-6 tracking-tight">
              Let's start a conversation
            </h2>
            <p className="text-lg text-slate-600 dark:text-text-secondary mb-10 leading-relaxed">
              Have a project in mind? Need consulting? We'd love to hear from you.
              Every conversation starts with understanding your vision and requirements.
            </p>

            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ x: 8 }}
                className="flex items-start gap-4 group"
              >
                <motion.div
                  whileHover={{ scale: 1.2, rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.3 }}
                >
                  <Mail size={20} className="text-primary mt-0.5" />
                </motion.div>
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-text-primary mb-1 group-hover:text-primary transition-colors">Email</h4>
                  <a
                    href="mailto:info@kaelivtech.com"
                    className="text-slate-600 dark:text-text-secondary hover:text-primary transition-colors"
                  >
                    info@kaelivtech.com
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ x: 8 }}
                className="flex items-start gap-4 group"
              >
                <motion.div
                  whileHover={{ scale: 1.2, rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.3 }}
                >
                  <Phone size={20} className="text-primary mt-0.5" />
                </motion.div>
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-text-primary mb-1 group-hover:text-primary transition-colors">Phone</h4>
                  <a
                    href="tel:+918487996556"
                    className="text-slate-600 dark:text-text-secondary hover:text-primary transition-colors"
                  >
                    +91 8487996556
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                whileHover={{ x: 8 }}
                className="flex items-start gap-4 group"
              >
                <motion.div
                  whileHover={{ scale: 1.2, rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.3 }}
                >
                  <Calendar size={20} className="text-primary mt-0.5" />
                </motion.div>
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-text-primary mb-1 group-hover:text-primary transition-colors">Schedule a meeting</h4>
                  <p className="text-slate-600 dark:text-text-secondary mb-3">Book a consultation according to our availability</p>
                  <motion.button
                    onClick={() => window.Calendly && window.Calendly.initPopupWidget({ url: 'https://calendly.com/kaelivtech-info' })}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-md transition-colors duration-200"
                  >
                    Request Meeting
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white dark:bg-slate-800/50 p-8 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-primary focus:outline-none text-slate-900 dark:text-text-primary transition-colors"
                  placeholder="John Doe"
                  disabled={status === 'sending'}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:ring-1 focus:ring-primary focus:outline-none text-slate-900 dark:text-text-primary transition-colors"
                  placeholder="john@example.com"
                  disabled={status === 'sending'}
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="4"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-primary focus:outline-none text-slate-900 dark:text-white transition-colors resize-none"
                  placeholder="Tell us about your project..."
                  disabled={status === 'sending'}
                ></textarea>
              </div>

              {/* Error Message */}
              {status === 'error' && errorMessage && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                  <AlertCircle size={20} />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              )}

              {/* Success Message */}
              {status === 'success' && (
                <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
                  <CheckCircle size={20} />
                  <p className="text-sm font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending' || status === 'success'}
                className={`w-full py-3 rounded-md font-medium text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${status === 'success'
                  ? 'bg-green-600'
                  : status === 'error'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary hover:bg-primary/90'
                  }`}
              >
                {status === 'sending' ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : status === 'success' ? (
                  <>
                    <CheckCircle size={20} /> Message Sent!
                  </>
                ) : (
                  <>
                    Send Message <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
};

export default Contact;